/**
 * 받는 쪽 거울 — FullState 로 시작해 스냅샷 델타를 틱별 상태로 쌓는다(셸 · 봇 · 시험이 같은 코드로 복원).
 * 받은 틱은 ackTick() 으로 서버에 알린다(그 틱이 다음 델타의 기준).
 */
import {
  decodeDelta,
  decodeFullStateV2,
  layoutOf,
  reconstructBodies,
  type EntityLayout,
  type TemplateBody,
} from "./entity";
import type { Ack } from "./index";

/** FullState 메타 중 복원에 쓰는 것 */
export interface MirrorMeta {
  readonly bodyCount: number;
  readonly machines: readonly {
    machine: number;
    bodyStart: number;
    bodyCount: number;
    template: number;
  }[];
  readonly templates: readonly (readonly TemplateBody[])[];
}

const KEEP = 128;

export class EntityMirror<M extends MirrorMeta = MirrorMeta> {
  readonly meta: M;
  readonly layouts: readonly EntityLayout[];
  readonly stateSize: number;
  /** 틱 → 받은 상태 */
  private readonly states = new Map<number, Int32Array>();
  /** 환경 몸체(FullState 에서만 온다) + 기계 몸체 틀 */
  private readonly base: Float32Array;
  latestTick: number;
  /** 기준이 없어 버린 스냅샷 수(정상이면 0) */
  missedBase = 0;

  private constructor(tick: number, meta: M, transforms: Float32Array, state: Int32Array) {
    this.meta = meta;
    const l = layoutOf(meta.machines, meta.templates);
    this.layouts = l.layouts;
    this.stateSize = l.stateSize;
    this.base = transforms;
    this.states.set(tick, state);
    this.latestTick = tick;
    reconstructBodies(state, this.layouts, this.base);
  }

  static fromFullState<M extends MirrorMeta>(
    bytes: Uint8Array,
  ): { mirror: EntityMirror<M>; tick: number } | null {
    const fs = decodeFullStateV2<M>(bytes, (m) => m.bodyCount);
    if (!fs) return null;
    return {
      mirror: new EntityMirror<M>(fs.tick, fs.meta, fs.transforms, fs.state),
      tick: fs.tick,
    };
  }

  /** 스냅샷 한 개 — 쌓은 틱 · 반영된 입력 · 바뀐 기계. 기준이 없으면 null(서버가 곧 절대값으로 보낸다) */
  apply(
    bytes: Uint8Array,
  ): { tick: number; flags: number; acks: Ack[]; machines: number[] } | null {
    const out = new Int32Array(this.stateSize);
    const d = decodeDelta(bytes, this.layouts, (t) => this.states.get(t) ?? null, out);
    if (!d) {
      this.missedBase++;
      return null;
    }
    this.states.set(d.tick, out);
    if (d.tick > this.latestTick) this.latestTick = d.tick;
    if (this.states.size > KEEP) {
      const oldest = this.states.keys().next().value;
      if (oldest !== undefined) this.states.delete(oldest);
    }
    return { tick: d.tick, flags: d.flags, acks: d.acks, machines: d.machines };
  }

  state(tick: number): Int32Array | null {
    return this.states.get(tick) ?? null;
  }

  /** 받은 틱들(오름차순) */
  ticks(): number[] {
    return [...this.states.keys()].sort((a, b) => a - b);
  }

  /** 틱의 몸체 자세(m · bodyCount × 7) — out 을 주면 거기에 */
  transforms(tick: number, out?: Float32Array): Float32Array | null {
    const s = this.states.get(tick);
    if (!s) return null;
    const t = out ?? new Float32Array(this.base.length);
    if (t !== this.base) t.set(this.base);
    reconstructBodies(s, this.layouts, t);
    return t;
  }
}
